import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import {
  Search,
  ChevronDown,
  ChevronUp,
  Clock,
  ArrowLeft,
  Send,
  Star,
  BookOpen,
  Users,
  Code,
  Rocket,
  Bot,
  Workflow,
  Plug,
  CreditCard,
  Wrench,
  X,
} from "lucide-react";
import { HELP_CATEGORIES, HELP_ARTICLES, FAQ_ITEMS, QUICK_LINKS } from "../data/helpArticles";

const ICON_MAP = {
  Rocket,
  Bot,
  Workflow,
  Plug,
  CreditCard,
  Wrench,
  BookOpen,
  Users,
  Code,
};

function CategoryIcon({ iconName, className }) {
  const Icon = ICON_MAP[iconName] || BookOpen;
  return <Icon className={className} />;
}

export default function HelpCenter() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Filter articles
  const filteredArticles = useMemo(() => {
    let articles = HELP_ARTICLES;
    if (activeCategory !== "all") {
      articles = articles.filter((a) => a.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      articles = articles.filter(
        (a) =>
          t(a.titleKey).toLowerCase().includes(q) ||
          t(a.contentKey).toLowerCase().includes(q) ||
          t(`help.category.${a.category}`).toLowerCase().includes(q)
      );
    }
    return articles;
  }, [activeCategory, searchQuery, t]);

  const popularArticles = useMemo(
    () => HELP_ARTICLES.filter((a) => a.popular),
    []
  );

  const handleCategoryChange = useCallback((catId) => {
    setActiveCategory(catId);
    setExpandedArticle(null);
  }, []);

  const toggleArticle = useCallback((articleId) => {
    setExpandedArticle((prev) => (prev === articleId ? null : articleId));
  }, []);

  const toggleFaq = useCallback((faqId) => {
    setExpandedFaq((prev) => (prev === faqId ? null : faqId));
  }, []);

  const handleFormSubmit = useCallback((e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setFormData({ name: "", email: "", message: "" });
    setTimeout(() => setFormSubmitted(false), 5000);
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <SEOHead
        title={t('help_center.help_center_max_os_cloud_support_guides')}
        description="Find answers to common questions about Max OS Cloud. Setup guides, troubleshooting, and support resources for AI digital workers."
        url="/help"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
              { "@type": "ListItem", position: 2, name: "Help Center", item: "https://maxemployee.com/help" },
            ],
          },
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-16 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">
            {t("help.section_label")}
          </div>
          <h1 className="font-display text-4xl md:text-5xl uppercase text-white tracking-wide mb-4">
            {t("help.title")}
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-8">
            {t("help.subtitle")}
          </p>

          {/* Search bar */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("help.search_placeholder")}
              className="w-full bg-zinc-900 border border-zinc-700 focus:border-amber-500 text-white pl-12 pr-12 py-4 font-mono text-sm placeholder:text-zinc-600 outline-none transition-colors rounded-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <aside className="lg:col-span-1">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
              {t("help.categories")}
            </h3>
            <nav className="space-y-1">
              <button
                onClick={() => handleCategoryChange("all")}
                className={`w-full text-left px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider transition-colors rounded-sm ${
                  activeCategory === "all"
                    ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`}
              >
                {t("help.all_articles")} ({HELP_ARTICLES.length})
              </button>
              {HELP_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`w-full flex items-center gap-3 text-left px-4 py-2.5 font-mono text-[11px] uppercase tracking-wider transition-colors rounded-sm ${
                    activeCategory === cat.id
                      ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500"
                      : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                  }`}
                >
                  <CategoryIcon iconName={cat.icon} className={`w-4 h-4 ${activeCategory === cat.id ? "text-amber-400" : cat.color}`} />
                  <span>{t(cat.nameKey)}</span>
                  <span className="ml-auto text-zinc-600">
                    {HELP_ARTICLES.filter((a) => a.category === cat.id).length}
                  </span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Main content */}
          <main className="lg:col-span-3">
            {/* Popular articles (show when no search and all category) */}
            {!searchQuery && activeCategory === "all" && (
              <section className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                  <Star className="w-4 h-4 text-amber-400" />
                  <h2 className="font-display text-lg uppercase text-white tracking-wide">
                    {t("help.popular_articles")}
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {popularArticles.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => toggleArticle(article.id)}
                      className="text-left bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 p-5 transition-colors rounded-sm group"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-500/70 mb-2">
                            {t(`help.category.${article.category}`)}
                          </div>
                          <h3 className="font-display text-sm uppercase text-white group-hover:text-amber-400 transition-colors mb-2">
                            {t(article.titleKey)}
                          </h3>
                          <div className="flex items-center gap-1.5 text-zinc-500">
                            <Clock className="w-3 h-3" />
                            <span className="font-mono text-[10px]">
                              {t("help.read_time", { minutes: article.readTime })}
                            </span>
                          </div>
                        </div>
                        <BookOpen className="w-5 h-5 text-zinc-700 group-hover:text-amber-500 transition-colors mt-1 shrink-0" />
                      </div>

                      {/* Expanded content */}
                      {expandedArticle === article.id && (
                        <div className="mt-4 pt-4 border-t border-zinc-800">
                          <p className="text-zinc-400 text-sm leading-relaxed">
                            {t(article.contentKey)}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedArticle(null);
                            }}
                            className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors flex items-center gap-1"
                          >
                            <ArrowLeft className="w-3 h-3" />
                            {t("help.close_article")}
                          </button>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Article list */}
            <section>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-lg uppercase text-white tracking-wide">
                  {searchQuery
                    ? t("help.search_results", { count: filteredArticles.length })
                    : activeCategory !== "all"
                    ? t(`help.category.${activeCategory}`)
                    : t("help.all_articles_title")}
                </h2>
                <span className="font-mono text-[10px] text-zinc-500">
                  {filteredArticles.length} {t("help.articles_count")}
                </span>
              </div>

              {filteredArticles.length === 0 ? (
                <div className="text-center py-16">
                  <Search className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <h3 className="font-display text-lg uppercase text-zinc-500 mb-2">
                    {t("help.no_results")}
                  </h3>
                  <p className="text-zinc-600 text-sm">
                    {t("help.no_results_desc")}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredArticles.map((article) => (
                    <div
                      key={article.id}
                      className="border border-zinc-800 hover:border-zinc-700 transition-colors rounded-sm"
                    >
                      <button
                        onClick={() => toggleArticle(article.id)}
                        className="w-full text-left p-5 flex items-start justify-between gap-4"
                      >
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-500/70">
                              {t(`help.category.${article.category}`)}
                            </span>
                            {article.popular && (
                              <span className="flex items-center gap-1 text-amber-400">
                                <Star className="w-3 h-3" />
                                <span className="font-mono text-[9px] uppercase">{t('help_center.popular')}</span>
                              </span>
                            )}
                          </div>
                          <h3 className="font-display text-sm uppercase text-white hover:text-amber-400 transition-colors">
                            {t(article.titleKey)}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-1.5 text-zinc-500">
                            <Clock className="w-3 h-3" />
                            <span className="font-mono text-[10px]">
                              {t("help.read_time", { minutes: article.readTime })}
                            </span>
                          </div>
                        </div>
                        <div className="shrink-0 mt-1">
                          {expandedArticle === article.id ? (
                            <ChevronUp className="w-5 h-5 text-amber-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-zinc-600" />
                          )}
                        </div>
                      </button>

                      {expandedArticle === article.id && (
                        <div className="px-5 pb-5 pt-0">
                          <div className="pt-4 border-t border-zinc-800">
                            <p className="text-zinc-400 text-sm leading-relaxed">
                              {t(article.contentKey)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* FAQ Section */}
            <section className="mt-16">
              <div className="text-center mb-8">
                <h2 className="font-display text-2xl uppercase text-white tracking-wide mb-2">
                  {t("help.faq_title")}
                </h2>
                <p className="text-zinc-500 text-sm">{t("help.faq_subtitle")}</p>
              </div>

              <div className="space-y-2">
                {FAQ_ITEMS.map((faq) => (
                  <div
                    key={faq.id}
                    className="border border-zinc-800 rounded-sm"
                  >
                    <button
                      onClick={() => toggleFaq(faq.id)}
                      className="w-full text-left p-5 flex items-start justify-between gap-4"
                    >
                      <h3 className="font-display text-sm uppercase text-white pr-4">
                        {t(faq.questionKey)}
                      </h3>
                      <div className="shrink-0 mt-0.5">
                        {expandedFaq === faq.id ? (
                          <ChevronUp className="w-5 h-5 text-amber-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-zinc-600" />
                        )}
                      </div>
                    </button>

                    {expandedFaq === faq.id && (
                      <div className="px-5 pb-5">
                        <p className="text-zinc-400 text-sm leading-relaxed">
                          {t(faq.answerKey)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Quick Links */}
            <section className="mt-16">
              <h2 className="font-display text-lg uppercase text-white tracking-wide mb-6">
                {t("help.quick_links_title")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {QUICK_LINKS.map((link) => (
                  <Link
                    key={link.id}
                    to={link.url}
                    className="bg-zinc-900/50 border border-zinc-800 hover:border-amber-500/30 p-6 transition-all group rounded-sm"
                  >
                    <CategoryIcon iconName={link.icon} className="w-8 h-8 text-amber-400 mb-4" />
                    <h3 className="font-display text-sm uppercase text-white group-hover:text-amber-400 transition-colors mb-2">
                      {t(link.titleKey)}
                    </h3>
                    <p className="text-zinc-500 text-xs leading-relaxed">
                      {t(link.descKey)}
                    </p>
                  </Link>
                ))}
              </div>
            </section>

            {/* Contact Support Form */}
            <section className="mt-16 mb-8">
              <div className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-sm">
                <div className="text-center mb-8">
                  <h2 className="font-display text-xl uppercase text-white tracking-wide mb-2">
                    {t("help.contact_title")}
                  </h2>
                  <p className="text-zinc-500 text-sm">
                    {t("help.contact_desc")}
                  </p>
                </div>

                {formSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="font-display text-lg uppercase text-emerald-400 mb-2">
                      {t("help.form_success")}
                    </h3>
                    <p className="text-zinc-500 text-sm">
                      {t("help.form_success_desc")}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="max-w-lg mx-auto space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                          {t("help.form_name")}
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleFormChange("name", e.target.value)}
                          required
                          className="w-full bg-zinc-950 border border-zinc-700 focus:border-amber-500 text-white px-4 py-3 font-mono text-sm placeholder:text-zinc-600 outline-none transition-colors rounded-sm"
                          placeholder={t("help.form_name_placeholder")}
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                          {t("help.form_email")}
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleFormChange("email", e.target.value)}
                          required
                          className="w-full bg-zinc-950 border border-zinc-700 focus:border-amber-500 text-white px-4 py-3 font-mono text-sm placeholder:text-zinc-600 outline-none transition-colors rounded-sm"
                          placeholder={t("help.form_email_placeholder")}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                        {t("help.form_message")}
                      </label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => handleFormChange("message", e.target.value)}
                        required
                        rows={4}
                        className="w-full bg-zinc-950 border border-zinc-700 focus:border-amber-500 text-white px-4 py-3 font-mono text-sm placeholder:text-zinc-600 outline-none transition-colors resize-none rounded-sm"
                        placeholder={t("help.form_message_placeholder")}
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-6 py-3.5 font-bold uppercase tracking-wider text-[11px] transition-colors rounded-sm"
                    >
                      <Send className="w-4 h-4" />
                      {t("help.form_submit")}
                    </button>
                  </form>
                )}
              </div>
            </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
